import React, { Fragment, useEffect, useState } from 'react';
import DayColumn from './DayColumn';
import TaskPopup from './TaskPopup';
import { createAvailability, deleteAvailability, updateAvailability } from '../api/CalendarApi';
import MonthSelect from '../MonthSelect';
import { loadBookings } from '../api/BookingsApi';
import { startEndDayMonthYear, getStartOfWeek, iterateDays } from './Utils';
import SvgIcon from '../../../common/SvgIcon';
import BookingPopup from './BookingPopup';
import { loadAvailabilities } from '../api/AdminAvailabilitiesApi';
import DayTimeColumn from './DayTimeColumn';
import {spamBooking, updateBooking} from '../api/AdminDesignersApi';
import CopyToClipboard from '../../../common/components/Button/ButtonCopyToClipboard';

const Calendar = ({ calendar, name, designer, availabilityUrl, date: initialDate, admin }) => {
  const [tasks, setTasks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showBookings, setShowBookings] = useState(true);

  const [startWeek, setStartWeek] = useState(getStartOfWeek(initialDate || new Date()));

  const [editTaskPopup, setEditTaskPopup] = useState();
  const [editBookingPopup, setEditBookingPopup] = useState();

  const daysToShow = 14;

  useEffect(() => {
    loadBookings(
      calendar, designer, startWeek,
      (data) => {
        const newBookings = data.bookings.map(booking => {
          return { ...booking, ...startEndDayMonthYear(booking) };
        });
        setBookings(newBookings);
      },
    );
  }, [startWeek, designer]);

  useEffect(() => {
    loadAvailabilities(
      calendar, designer, startWeek,
      (data) => {
        const newAvailabilities = data.availabilities.map(availability => {
          return { ...availability, ...startEndDayMonthYear(availability) };
        });
        setTasks(newAvailabilities);
      },
    );
  }, [startWeek, designer]);

  const handleCalendarMonthChange = newDate => {
    setStartWeek(newDate);
  };

  function updateTaskWithParams(id, data) {
    setTasks(prev => {
      const idx = prev.findIndex(el => el.id === id);
      const newAvailabilities = [...prev];
      const { availability: newAvailability } = data;
      newAvailabilities[idx] = { ...newAvailability, ...startEndDayMonthYear(newAvailability) };
      return newAvailabilities;
    });
  }

  const onAddAvailability = (kind, start, end) => {
    if (admin) return;

    const day = start.getDate();
    const year = start.getFullYear();
    const month = start.getMonth();
    const tempId = -Math.floor(Math.random() * 10000000.0);
    setTasks(prev => [
      ...prev,
      { id: tempId, start, end, day, month, year, kind, loading: true },
    ]);

    const availability = {
      time_from: start,
      time_to: end,
      kind,
    };

    createAvailability(
      calendar,
      availability,
      data => {
        updateTaskWithParams(tempId, data);
      },
      error => console.log(error),
    );
  };

  const handleTaskClick = task => {
    setEditTaskPopup(task);
  };

  const handleBookingClick = booking => {
    setEditBookingPopup(booking);
  };

  function markLoading(task, toggle = true) {
    setTasks(prev => {
      const newTasks = [...prev];
      const taskToRemove = newTasks.find(i => i.id === task.id);
      taskToRemove.loading = toggle;
      return newTasks;
    });
  }

  const onUpdateTask = task => {
    markLoading(task);
    const updatedTask = {...task, time_from: task.start, time_to: task.end};
    setEditTaskPopup(null);
    updateAvailability(
      updatedTask,
      (response) => {
        markLoading(task, false);
        updateTaskWithParams(task.id, response);
        // console.log(`task updated:`, task);
      },
      () => {},
    );
  };

  const onDeleteTask = task => {
    markLoading(task);
    setEditTaskPopup(null);
    deleteAvailability(
      task,
      (response) => {
        setTasks(prev => prev.filter(i => i.id !== task.id));
        // console.log(`task deleted:`, task);
      },
      () => {},
    );
  };

  const onSpamBooking = updatedBooking => {
    const { reassigned_designer } = updatedBooking;
    spamBooking(
      calendar,
      updatedBooking,
      reassigned_designer,
      data => {
        const { booking } = data;
        const newBookings = [...bookings];
        const idx = bookings.findIndex(i => i.id = booking.id);
        if (idx >= 0) {
          newBookings[idx] = { ...booking, ...startEndDayMonthYear(booking) };
          setBookings(newBookings);
        }
      },
      error => {
        console.log(error);
      },
    );
    // console.log(`booking to update:`, updatedBooking);
  };
  const onUpdateBooking = updatedBooking => {
    const { reassigned_designer } = updatedBooking;
    updateBooking(
      calendar,
      updatedBooking,
      reassigned_designer,
      data => {
        const { booking } = data;
        const newBookings = [...bookings];
        const idx = bookings.findIndex(i => i.id = booking.id);
        if (idx >= 0) {
          newBookings[idx] = { ...booking, ...startEndDayMonthYear(booking) };
          setBookings(newBookings);
        }
      },
      error => {
        console.log(error);
      },
    );
    // console.log(`booking to update:`, updatedBooking);
  };

  const dayIterrator = iterateDays(startWeek, daysToShow);

  return (
    <Fragment>
      <div>
        <div className="calendar-header">
          <h2 className="title">
            <span>{name || 'My Calendar'}</span>
            { availabilityUrl && (
              <CopyToClipboard className='btn -white -y2023' data={availabilityUrl}>
                <SvgIcon icon='icon-link' />
              </CopyToClipboard>
            )}
          </h2>
          <MonthSelect handleChange={handleCalendarMonthChange} />
        </div>
      </div>
      <div className="designer-personal-calendar">
        <DayTimeColumn />
        <div className={'scrollWrapper'}>
          {dayIterrator.map(day => (
            <DayColumn
              calendar={calendar}
              key={day}
              day={day.getDate()}
              year={day.getFullYear()}
              month={day.getMonth()}
              tasks={tasks.filter(task => task.day === day.getDate() && task.month === day.getMonth() && task.year === day.getFullYear())}
              bookings={showBookings ? bookings.filter(booking => booking.day === day.getDate() && booking.month === day.getMonth() && booking.year === day.getFullYear()) : []}
              onAddAvailability={onAddAvailability}
              onTaskClick={handleTaskClick}
              onBookingClick={handleBookingClick}
            />
          ))}
        </div>
      </div>
      {editTaskPopup && (
        <TaskPopup admin={admin} task={editTaskPopup} onUpdate={onUpdateTask} onDelete={onDeleteTask}
                   onCloseModal={() => setEditTaskPopup(null)} />
      )}
      {editBookingPopup && (
        <BookingPopup admin={admin} calendar={calendar} booking={editBookingPopup} onUpdate={onUpdateBooking} onSpam={onSpamBooking}
                   onCloseModal={() => setEditBookingPopup(null)} />
      )}
    </Fragment>
  );
};

export default Calendar;



// WEBPACK FOOTER //
// ./app/javascript/components/designer_calendars/components/Calendar.js